/**
 * Presentational pieces for the Meeting Room Select — per-option row,
 * single-value trigger, and multi-select chip.
 *
 * Matches the Figma "Meeting room" recipe (`CustomRenderOptionWithGroups[Multi]`
 * in `@kula-ai/components/.../SelectVariations.stories.tsx`). Kept free of
 * any availability fetch logic — the caller (DetailsForm) assembles the
 * grouped options with `data: { status, capacity }` and passes these
 * renders down through the FormBuilder meeting-room config.
 */
import { Box, Flex, Icon } from '@chakra-ui/react';
import { Users01 } from '@kula-ai/icons/dual-tone/Users01';
import { XClose } from '@kula-ai/icons/outline/XClose';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { Checkbox, Tag, type SelectOption } from '@kula-ai/components';
import { KText } from '@/ui-library/components/text';
import type { MeetingRoomOptionData } from '@/formConfigs';

function getMeta(option: SelectOption): MeetingRoomOptionData | undefined {
  return option?.data as MeetingRoomOptionData | undefined;
}

function statusDotColor(status?: MeetingRoomOptionData['status']): string {
  return status === 'busy'
    ? 'var(--kula-colors-error-200)'
    : 'var(--kula-colors-success-200)';
}

export function renderMeetingRoomOption(
  option: SelectOption,
  { isSelected }: { isSelected: boolean }
) {
  const meta = getMeta(option);
  return (
    <Flex w="100%" alignItems="flex-start" gap="3">
      <Checkbox
        checked={isSelected}
        size="sm"
        tabIndex={-1}
        readOnly
        style={{ pointerEvents: 'none', marginTop: 2 }}
      />
      <Flex flex="1" minW={0} flexDirection="column" gap="1">
        <Flex alignItems="center" gap="2">
          <KText
            variant="small_text"
            color="secondary-900"
            fontWeight="600"
            noOfLines={1}
          >
            {option.label}
          </KText>
          {meta?.status === 'available' ? (
            <Tag label="Available" variant="success" size="sm" />
          ) : meta?.status === 'busy' ? (
            <Tag label="Busy" variant="error" size="sm" />
          ) : null}
        </Flex>
        {typeof meta?.capacity === 'number' && (
          <Flex alignItems="center" gap="1">
            <Icon as={Users01} w="12px" h="12px" color="secondary-500" />
            <KText color="secondary-500">{meta.capacity}</KText>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}

export function renderMeetingRoomTriggerValue(
  selected: SelectOption | SelectOption[] | null | undefined
) {
  const opt = Array.isArray(selected) ? selected[0] : selected;
  if (!opt) return null;
  const meta = getMeta(opt);
  return (
    <Flex alignItems="center" gap="2">
      <Box
        w="8px"
        h="8px"
        borderRadius="full"
        bg={statusDotColor(meta?.status)}
      />
      <KText variant="small_text">{opt.label}</KText>
    </Flex>
  );
}

interface AccordionSection {
  label: string;
  options: SelectOption[];
}

interface AccordionState {
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * Custom accordion header — office name on the left, room count +
 * chevron on the right. Matches the components package's
 * `AccordionRenderGroupHeaderStory` escape hatch so we can control the
 * look entirely without touching the shared package.
 */
export function renderMeetingRoomGroupHeader(
  section: AccordionSection,
  { isExpanded, onToggle }: AccordionState
) {
  const count = section.options.length;
  return (
    <Flex
      as="button"
      type="button"
      onClick={onToggle}
      w="100%"
      align="center"
      justify="space-between"
      px="3"
      py="2"
      mb="1"
      borderRadius="md"
      bg="primary-25"
      cursor="pointer"
    >
      <KText
        variant="small_text"
        fontWeight="650"
        color="secondary-900"
        textTransform="uppercase"
        letterSpacing="wide"
      >
        {section.label}
      </KText>
      <Flex align="center" gap="2">
        <KText color="secondary-500">
          {count} {count === 1 ? 'room' : 'rooms'}
        </KText>
        <Icon
          as={ChevronDown}
          w="12px"
          h="12px"
          color="secondary-500"
          transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
          transition="transform 180ms ease"
        />
      </Flex>
    </Flex>
  );
}

export function renderMeetingRoomTag(
  option: SelectOption,
  { onRemove }: { onRemove: () => void }
) {
  const meta = getMeta(option);
  return (
    <Flex
      as="span"
      alignItems="center"
      gap="2"
      m="1"
      px="2"
      py="0.5"
      borderRadius="md"
      border="1px solid"
      borderColor="secondary-100"
      bg="white"
    >
      <Box
        w="8px"
        h="8px"
        borderRadius="full"
        bg={statusDotColor(meta?.status)}
      />
      <KText variant="small_text" color="secondary-700" noOfLines={1}>
        {option.label}
      </KText>
      <Box
        as="button"
        type="button"
        tabIndex={-1}
        onMouseDown={(e: React.MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }}
        opacity={0.7}
        _hover={{ opacity: 1 }}
        display="inline-flex"
        cursor="pointer"
        aria-label={`Remove ${option.label}`}
      >
        <Icon as={XClose} w="12px" h="12px" />
      </Box>
    </Flex>
  );
}
